// Dependencies
import { Component } from 'react';
import { connect } from 'react-redux';
import { Tooltip } from 'react-tooltip';

import Icon from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import LoaderSpinner from 'components/atoms/LoaderSpinner';
import MenuGroup from 'components/atoms/MenuGroup';
import { TIME_MODE_RANG_HOURS } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { trackByDate, trackByVessel } from 'services/tracks';
import {
    SetErrorHandlerAction,
    setErrorHandlerAction,
    SetSystemLoadingAction,
    setSystemLoadingAction,
} from 'store/actions/errorsActions';
import {
    SetLayoutAction,
    setLayoutAction,
    SetMenuControlsAction,
    setMenuControlsAction,
} from 'store/actions/layoutActions';
import {
    InterfaceVariablesType,
    Layout,
    OpenModal,
    PortShipType,
    PortType,
    RootState,
    SwitchType,
    UserType,
} from 'types';
import {
    ChangeDateRangeInterface,
    GetBestPositionInterface,
    GetDateInterface,
    GetTrackInterface,
    MainControlsInterface,
    MapControlsInterface,
    SetSelectedTimeInterface,
    UpdateTrackInterface,
} from 'types/functions';
import { SystemType } from 'types/system';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { findNearestPoint } from 'utils/drawing';
import { checkDifferent, checkLayoutDifferent, clone, getId } from 'utils/formHandlers';
import geometry from 'utils/geometry';
import { getFlagImage, getFlagName } from 'utils/getVesselInfo';
import {
    copyTracksOptions,
    findBestData,
    findBestPosition,
    flatTrackDate,
    formatTracks,
    isVesselMoving,
} from 'utils/migrations';
import { hasPermission } from 'utils/permission';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

interface TimeVesselsTabComponentProps {
    active: boolean;
    port: PortType;
    layout: Layout;
    setLayoutAction: SetLayoutAction;
    switchs: SwitchType;
    mainControls: MainControlsInterface;
    setErrorHandlerAction: SetErrorHandlerAction;
    openModelFunction: OpenModal;
    user: UserType;
    setMenuControlsAction: SetMenuControlsAction;
    setSystemLoadingAction: SetSystemLoadingAction;
    mapControls: MapControlsInterface;
    system: SystemType;
    dateTimeFormat: DateTimeFormat;
}

interface TimeVesselsTabComponentState {
    active: boolean;
    layout: Layout;
    switchs: SwitchType;
    dateChange: boolean;
    system: SystemType;
    groups: {
        id: string;
        name: string;
        active: boolean;
        total: number;
        displayMoving?: boolean;
        trackLoadedMoving?: boolean;
        drawTrackMoving?: boolean;
        displayNotMoving?: boolean;
        trackLoadedNotMoving?: boolean;
        drawTrackNotMoving?: boolean;
    }[];
    tracks: any[];
    startTime: DateTimeFormat;
    endTime: DateTimeFormat;
    interfaceVariables: InterfaceVariablesType;
    selectedTime?: DateTimeFormat | null;
    portShips?: PortShipType[];
    transitShips?: PortShipType[];
    minimumLength: number;
    loading: boolean;
}

class TimeVesselsTab extends Component<TimeVesselsTabComponentProps, TimeVesselsTabComponentState> {
    public geometry;
    constructor(props: TimeVesselsTabComponentProps) {
        super(props);
        this.state = {
            dateChange: false,
            layout: clone(props.layout),
            active: props.active,
            switchs: clone(props.switchs),
            loading: true,
            tracks: [],
            groups: [
                { name: 'transitVessels', id: getId(), active: true, total: 0 },
                { name: 'portVessels', id: getId(), active: true, total: 0 },
                { name: 'otherVessels', id: getId(), active: false, total: 0 },
            ],
            system: props.system,
            portShips: props.port.port_ships,
            transitShips: props.port.transit_ships,
            minimumLength: props.port.minimum_length | 40,

            // startTime: props.system.date.today.clone().roundToNearest30Minutes().minus({ hours: TIME_MODE_RANG_HOURS }),
            // endTime: props.system.date.today.clone().roundToNearest30Minutes(),
            startTime: props.system.date.today.clone().roundToNearest5Minutes().minus({ hours: TIME_MODE_RANG_HOURS }),
            endTime: props.system.date.today.clone().roundToNearest5Minutes(),
            selectedTime: props.dateTimeFormat
                .now()
                .roundToNearest5Minutes()
                .minus({ minutes: props.layout.viewMode === 'live' ? 5 : 10 }),
            interfaceVariables: props.port.meta_data.interfaceVariables,
        };
        //@ts-ignore
        this.geometry = new geometry(props.port?.projection, 'EPSG:4326');
    }

    static getDerivedStateFromProps(props: TimeVesselsTabComponentProps, state: TimeVesselsTabComponentState) {
        if (
            props.active !== state.active ||
            checkLayoutDifferent(props.layout, state.layout, ['sections.time.menus.timeMenu'])
        ) {
            return { layout: clone(props.layout), active: props.active };
        }
        if (checkDifferent(props.system, state.system)) {
            return { system: props.system };
        }
        if (checkDifferent(props.switchs, state.switchs)) {
            return {
                switchs: clone(props.switchs),
            };
        }

        return null;
    }

    componentDidMount(): void {
        this.props.setMenuControlsAction({
            time: {
                getTrack: this.getTrack,
                getBestPosition: this.getBestPosition,
                changeDateRange: this.changeDateRange,
                getDate: this.getDate,
                refresh: this.refresh,
                setSelectedTime: this.setSelectedTime,
                trackDisplay: this.trackDisplay,
                trackDrawTrack: this.trackDrawTrack,
            },
        });
        if ((this.props.active && this.state.layout.viewMode === 'live') || this.state.layout.viewMode === 'live') {
            this.loadTimeData(true, true);
        }
    }

    componentDidUpdate(
        prevProps: Readonly<TimeVesselsTabComponentProps>,
        prevState: Readonly<TimeVesselsTabComponentState>,
        snapshot?: any
    ): void {
        if (
            prevState.layout.viewMode !== 'live' &&
            this.state.layout.viewMode === 'live' &&
            this.state.active &&
            this.state.tracks.length === 0
        ) {
            this.loadTimeData(true, true);
        }

        if (
            (prevState.startTime.toTimestamp !== this.state.startTime.toTimestamp ||
                prevState.endTime.toTimestamp !== this.state.endTime.toTimestamp) &&
            this.state.active
        ) {
            this.setTracks();
        }

        if (checkDifferent(this.state.tracks, prevState.tracks)) {
            this.updateGroups();
        }
    }

    changeDateRange: ChangeDateRangeInterface = (startTime, endTime, selectedTime = null) => {
        console.log({ startTime, endTime, selectedTime });
        this.setState(
            {
                dateChange: true,
                startTime,
                selectedTime,
                endTime,
                tracks: [],
                loading: true,
            },
            () => {
                this.props.mapControls.time?.updateTracks(
                    [],
                    //@ts-ignore
                    selectedTime,
                    this.state?.startTime,
                    this.state?.endTime,
                    undefined
                );
                this.loadTimeData(false, false);
            }
        );
    };

    setSelectedTime: SetSelectedTimeInterface = (selectedTime) => {
        this.setState({
            selectedTime,
        });
    };

    getDate: GetDateInterface = () => {
        return this.state.startTime;
    };

    getBestPosition: GetBestPositionInterface = (id, positionId, target, dataType) => {
        const track = this.getTrack(id);
        //@ts-ignore
        const { data, type } = findBestData(track?.data, null, null, dataType);
        if (positionId) {
            return data.find((x) => {
                return x.id === positionId;
            });
        }

        if (target) {
            return findNearestPoint(data, target, null, null);
        }

        return data.find((item) => {
            //@ts-ignore
            return item.valid_time_utc.toTimestamp() <= this.props.mapControls.time?.getCurrentTime()?.toTimestamp();
        });
    };

    getTrack: GetTrackInterface = (id: string | number) => {
        return this.state.tracks.find((x) => {
            return x.uuid === id;
        });
    };

    refresh = (updateTime = false, intial = false) => {
        this.props.setLayoutAction([
            {
                action: 'UPDATE_DATA',
                data: {
                    section: 'time',
                    data: {
                        startTime: this.props.system.date.today
                            .clone()
                            .roundToNearest5Minutes()
                            .minus({ hours: TIME_MODE_RANG_HOURS }),
                        endTime: this.props.system.date.today.clone().roundToNearest5Minutes(),
                    },
                },
            },
        ]);

        this.setState(
            {
                startTime: this.props.system.date.today
                    .clone()
                    .roundToNearest5Minutes()
                    .minus({ hours: TIME_MODE_RANG_HOURS }),
                endTime: this.props.system.date.today.clone().roundToNearest5Minutes(),
                loading: true,
            },
            () => {
                this.loadTimeData(updateTime, intial);
            }
        );
    };

    updateGroupState = (value: boolean, index: number) => {
        this.setState({
            groups: this.state.groups.map((v, i) => {
                if (i === index) {
                    v.active = value;
                }
                return v;
            }),
        });
    };

    trackDrawTrackGroup = (name: string, moving: boolean | null, value) => {
        this.updateOptions(name, moving, 'drawTrack', value);
    };

    trackDisplayGroup = (name: string, moving: boolean | null, value: boolean) => {
        this.updateOptions(name, moving, 'display', value);
    };

    updateOptions(group: string | null, moving: boolean | null, key: string, value: boolean) {
        this.setState(
            {
                tracks: this.state.tracks.map((track) => {
                    if (
                        (track.options.group === group || group === null) &&
                        (track.options.moving === moving || moving === null)
                    ) {
                        return {
                            ...track,
                            options: {
                                ...track.options,
                                loading: key === 'display' && !track.options.trackLoaded,
                                [key]: value === null ? !track.options[key] : value,
                            },
                        };
                    }

                    return track;
                }),
            },
            () => {
                this.loadVesselsData(false, false);
            }
        );
    }

    loadTimeData = (updateTime = false, intial = false) => {
        console.log('Start time mode', updateTime, intial);
        trackByDate(this.props.port.id, {
            endDate: this.state.endTime?.toIso(),
            id: 0,
            mode: 'live',
            startDate: this.state.startTime?.toIso(),
        })
            .then((res) => {
                this.setState(
                    {
                        tracks: copyTracksOptions(
                            formatTracks(
                                null,
                                0,
                                res.items,
                                this.props.switchs,
                                this.state.interfaceVariables,
                                this.state.minimumLength,
                                this.state.portShips,
                                this.state.transitShips,
                                'time'
                            ),
                            this.state.groups
                        ),
                    },
                    () => {
                        this.loadVesselsData(updateTime, intial);
                    }
                );
            })
            .catch((errors) => {
                // Handle errors
                this.props.setErrorHandlerAction(errors);
            });
    };

    loadVesselData = async (id) => {
        try {
            const results = await trackByVessel(this.props.port.id, id, {
                endDate: this.state.endTime?.toIso(),
                id: 0,
                mode: 'time',
                startDate: this.state.startTime?.toIso(),
            });
            return { results, status: true }; // Return the resolved data
        } catch (errors) {
            return { results: {}, status: false };
        }
    };

    loadVesselsData = async (updateTime: boolean, intial: boolean) => {
        try {
            const vesselResults = await Promise.all(
                this.state.tracks.map(async (track) => {
                    if (!track.options.trackLoaded && track.options.display) {
                        const { results, status } = await this.loadVesselData(track.vessel_id);
                        return { track, results, status };
                    }
                    return { track, results: null, status: true }; // already loaded, treat as success
                })
            );
            console.log(vesselResults);
            if (vesselResults.filter((r) => !r.status).length === vesselResults.length && vesselResults.length > 0) {
                this.setState({
                    tracks: this.state.tracks.map((track) => {
                        return {
                            ...track,
                            options: {
                                ...track.options,
                                loading: false,
                            },
                        };
                    }),
                });
            } else if (vesselResults.filter((r) => !r.status).length > 0 && vesselResults.length > 0) {
                this.props.openModelFunction(
                    'loadTimeModeVesselErrorModel',
                    0,
                    'medium',
                    {},
                    {
                        onCancel: () => {
                            this.props.openModelFunction('loadTimeModeVesselErrorModel');
                        },
                        onConfirm: () => {
                            this.setState(
                                {
                                    loading: true,
                                },
                                () => {
                                    this.loadTimeData(true, true);
                                }
                            );
                        },
                    },
                    true
                );
            }

            // Merge successful results
            const mergedTracks = vesselResults.map(({ track, results }) => {
                if (!results || !results?.items || !results.items?.[`v-${track.vessel_id}`]) {
                    return {
                        ...track,
                        options: {
                            ...track.options,
                            loading: false,
                        },
                    };
                }

                return {
                    ...track,
                    data: {
                        events: [],
                        offsets: results.items[`v-${track.vessel_id}`]?.offsets,
                        smoothedId: getId(),
                        smoothed: flatTrackDate(
                            this.props.dateTimeFormat,
                            results.items[`v-${track.vessel_id}`]?.smoothed?.data
                        )
                            .sort((a, b) => a.valid_time_utc.toTimestamp() - b.valid_time_utc.toTimestamp())
                            ?.map((position) => ({
                                ...position,
                                valid_time_utc: position.valid_time_utc,
                            })),
                        centroidId: getId(),
                        centroid: flatTrackDate(
                            this.props.dateTimeFormat,
                            results.items[`v-${track.vessel_id}`]?.centroid?.data
                        )
                            .sort((a, b) => a.valid_time_utc.toTimestamp() - b.valid_time_utc.toTimestamp())
                            ?.map((position) => ({
                                ...position,
                                valid_time_utc: position.valid_time_utc,
                            })),
                        rawAISId: getId(),
                        rawAIS: flatTrackDate(
                            this.props.dateTimeFormat,
                            results.items[`v-${track.vessel_id}`]?.rawAIS?.data
                        )
                            .sort((a, b) => a.valid_time_utc.toTimestamp() - b.valid_time_utc.toTimestamp())
                            ?.map((position) => ({
                                ...position,
                                valid_time_utc: position.valid_time_utc,
                            })),
                    },
                    options: {
                        ...track.options,
                        moving: isVesselMoving(results.items[`v-${track.vessel_id}`]?.rawAIS?.data),
                        loading: false,
                        trackLoaded: true,
                        rawAISLoaded: true,
                        centroidLoaded: true,
                        smoothedLoaded: true,
                    },
                };
            });

            this.setView(mergedTracks);

            let trackTime = this.state.selectedTime;
            if (updateTime) {
                trackTime = this.state?.endTime.clone();
                if (intial) {
                    const lastMessageTime = this.props.dateTimeFormat.parseDateTime(
                        this.props.system.update.messages.valid_time_utc
                    );
                    if (lastMessageTime.isBetween(this.state?.startTime.date, this.state?.endTime.date)) {
                        trackTime = lastMessageTime;
                    } else {
                        console.log('The last message time is outside last hour date range');
                    }
                }
            }

            this.setState({ tracks: mergedTracks, selectedTime: trackTime }, async () => {
                await this.props.mapControls.time?.updateTracks(
                    mergedTracks,
                    //@ts-ignore
                    trackTime,
                    this.state?.startTime,
                    this.state?.endTime,
                    undefined
                );

                this.setState({ loading: false });

                this.props.setLayoutAction([
                    { action: 'UPDATE_SECTION', data: { name: 'replay', loading: false } },
                    { action: 'UPDATE_SECTION', data: { name: 'time', loading: false } },
                ]);
            });
        } catch (errors) {
            // Centralized failure handler
            this.setState({ loading: false });
            this.props.setLayoutAction([
                { action: 'UPDATE_SECTION', data: { name: 'replay', loading: false } },
                { action: 'UPDATE_SECTION', data: { name: 'time', loading: false } },
            ]);
            this.props.openModelFunction(
                'loadTimeModeVesselErrorModel',
                0,
                'medium',
                {},
                {
                    onCancel: () => {
                        this.props.openModelFunction('loadTimeModeVesselErrorModel');
                    },
                    onConfirm: () => {
                        this.setState(
                            {
                                loading: true,
                            },
                            () => {
                                this.loadTimeData(true, true);
                            }
                        );
                    },
                },
                true
            );
        }
    };

    setView = (tracks: any[]) => {
        const positions: any[] = [];
        const portArea = this.props.port?.port_areas?.find((y) => {
            return y.type === 'portArea';
        });
        let polygon = null;
        if (portArea) {
            polygon = this.geometry.transformerGEOJSON(this.geometry.parseWKB(portArea.geo));
        }

        tracks.forEach((x) => {
            const { position } = findBestPosition(
                x.data.rawAIS || [],
                'rawAIS',
                x.data.rawAIS || [],
                //@ts-ignore
                this.state.selectedTime
            );

            if (
                (position && !polygon) ||
                (position &&
                    polygon &&
                    this.geometry.isPointInPolygon(
                        {
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: this.geometry.transformer([position.east_utm, position.north_utm]),
                            },
                        },
                        polygon
                    ))
            ) {
                positions.push(position);
            }
        });

        if (!positions.length) return;

        this.props.mapControls.time?.setViewBounds(
            positions.map((x) => {
                return this.geometry.transformer([x.east_utm, x.north_utm]);
            }),
            600
        );
    };

    updateGroups = () => {
        console.log('updateGroups');

        this.setState((prevState) => {
            const { tracks, groups } = prevState;

            const updatedGroups = groups.map((group) => {
                const groupTracks = tracks.filter((t) => t.options.group === group.name);
                const movingTracks = groupTracks.filter((t) => t.options.moving);
                const notMovingTracks = groupTracks.filter((t) => !t.options.moving);

                const computeState = (data, key) => {
                    // if (data.length === 0) return false;
                    return data.every((x) => x.options[key]);
                };

                return {
                    ...group,
                    total: groupTracks.length,
                    displayMoving: computeState(movingTracks, 'display'),
                    drawTrackMoving: computeState(movingTracks, 'drawTrack'),
                    trackLoadedMoving: computeState(movingTracks, 'trackLoaded'),
                    displayNotMoving: computeState(notMovingTracks, 'display'),
                    drawTrackNotMoving: computeState(notMovingTracks, 'drawTrack'),
                    trackLoadedNotMoving: computeState(notMovingTracks, 'trackLoaded'),
                };
            });

            return { groups: updatedGroups };
        });
    };

    setTracks = () => {
        this.setState(
            {
                loading: true,
            },
            async () => {
                if (this.state.tracks.length) {
                    await this.props.mapControls.time?.updateTracks(
                        this.state.tracks,
                        //@ts-ignore
                        this.state.selectedTime,
                        this.state?.startTime,
                        this.state?.endTime,
                        undefined
                    );
                }
                // this.setState({
                //     loading: false,
                // });
            }
        );
    };

    trackDrawTrack: UpdateTrackInterface = async (uuid: string, value) => {
        await this.updateOption(uuid, 'drawTrack', value);
    };

    trackDisplay: UpdateTrackInterface = async (uuid: string, value) => {
        await this.updateOption(uuid, 'display', value);
    };

    async updateOption(uuid: string | null, key: string, value: boolean) {
        await new Promise<void>((resolve) => {
            this.setState(
                {
                    tracks: this.state.tracks.map((track) => {
                        if (track.uuid === uuid || uuid === null) {
                            return {
                                ...track,
                                options: {
                                    ...track.options,
                                    loading: key === 'display' && !track.options.trackLoaded,
                                    [key]: value === null ? !track.options[key] : value,
                                },
                            };
                        }

                        return track;
                    }),
                },
                () => {
                    this.loadVesselsData(false, false);
                    resolve();
                }
            );
        });

        // Now the state is updated and loadVesselsData has completed.
        // You can safely continue from here
    }

    goToVesel = (uuid: string) => {
        this.props.mapControls.time?.goToVesselLocation(uuid);
    };

    lastAISUpdate = () => {
        return (
            this.props.dateTimeFormat
                .parseDateTime()
                .getDiffInSeconds(this.state.system.update?.messages.valid_time_utc) / 60
        );
    };

    changeTime = () => {
        this.props.openModelFunction(
            'changeTimeModel',
            0,
            'custom',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('changeTimeModel');
                },
                onConfirm: () => {
                    //console.log('saved');
                },
            },
            true
        );
    };

    render() {
        return (
            <div className={twclsx(!this.state.active && 'hidden')}>
                {this.state.loading ? (
                    <LoaderSpinner />
                ) : (
                    <>
                        <div>
                            <div className="px-2 py-2 border-b flex justify-between border-gray-200">
                                <div>{this.state?.selectedTime?.formatDateTimeToString('dd MMM yyyy HH:mm:ss Z')}</div>
                                <div className="flex gap-1 hidden">
                                    <div
                                        data-tooltip-id="refreshTimeMenu"
                                        onClick={() => {
                                            this.refresh();
                                        }}
                                    >
                                        <Icon className="w-4 h-4 cursor-pointer ">
                                            <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"></path>
                                            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"></path>
                                        </Icon>

                                        <Tooltip
                                            id="refreshTimeMenu"
                                            html={`${translate('Get lastest AIS data')}`}
                                            place={'left'}
                                            positionStrategy="fixed"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {this.state.groups
                            .filter((group) => {
                                return this.state.layout.sections.time.menus.timeMenu.sections[group.name].state;
                            })
                            .map((group, index) => {
                                return (
                                    <MenuGroup
                                        active={true}
                                        // active={group.active}
                                        key={group.id}
                                        // callback={(value: boolean) => {
                                        //     this.updateGroupState(value, index);
                                        // }}
                                        sticky
                                        title={`${group.total} ${translate('vessels')}`}
                                    >
                                        <div>
                                            {this.state.tracks
                                                .filter((x) => x.options.group === group.name)
                                                .sort((a, b) => {
                                                    if (a.vessel.name < b.vessel.name) return -1;
                                                    if (a.vessel.name > b.vessel.name) return 1;
                                                    return 0;
                                                })

                                                .filter((x) => x.options?.moving)
                                                .map((x, i) => {
                                                    return (
                                                        <>
                                                            {i === 0 && (
                                                                <div className="bg-gray-100 raw border-b raw relative  border-t border-gray-300 py-1 flex justify-between  ">
                                                                    <div className=" flex items-center gap-1">
                                                                        <div>
                                                                            <Icon>
                                                                                <path d="M2 20a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1"></path>
                                                                                <path d="M4 18l-1 -5h18l-2 4"></path>
                                                                                <path d="M5 13v-6h8l4 6"></path>
                                                                                <path d="M7 7v-4h-1"></path>
                                                                            </Icon>
                                                                        </div>
                                                                        <div className="font-bold ">
                                                                            {translate('Underway vessels')}
                                                                        </div>
                                                                    </div>
                                                                    <div className="rawActionsStaticRight hidden right-0 h-full flex justify-items-center gap-1 pt-2 pr-1 ">
                                                                        <div>
                                                                            {!group.displayMoving ? (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {!group.drawTrackMoving ? (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <div className="rawActionsRight right-0 h-full flex justify-items-center gap-1 pt-2 pr-1 pl-10 ">
                                                                        <div>
                                                                            {!group.displayMoving ? (
                                                                                <div
                                                                                    data-tooltip-id={`displayGroupUnderwayVessels`}
                                                                                    onClick={() => {
                                                                                        this.trackDisplayGroup(
                                                                                            group.name,
                                                                                            true,
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        id="displayGroupUnderwayVessels"
                                                                                        html={`${translate(
                                                                                            'Show all vessels'
                                                                                        )}`}
                                                                                        className="z-20"
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    data-tooltip-id={`displayGroupUnderwayVessels`}
                                                                                    onClick={() => {
                                                                                        this.trackDisplayGroup(
                                                                                            group.name,
                                                                                            true,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        id="displayGroupUnderwayVessels"
                                                                                        html={`${translate(
                                                                                            'Hide all vessels'
                                                                                        )}`}
                                                                                        className="z-20"
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {!group.drawTrackMoving ? (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrackGroup(
                                                                                            group.name,
                                                                                            true,
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`trackGroupUnderwayVessels`}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-20"
                                                                                        id="trackGroupUnderwayVessels"
                                                                                        html={`${translate(
                                                                                            'Show all vessels tracks'
                                                                                        )}`}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrackGroup(
                                                                                            group.name,
                                                                                            true,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`trackGroupUnderwayVessels`}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        className="z-20"
                                                                                        id="trackGroupUnderwayVessels"
                                                                                        html={`${translate(
                                                                                            'Hide all vessels tracks'
                                                                                        )}`}
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div
                                                                key={x.uuid}
                                                                className="flex justify-between border-b border-gray-300 relative pb-1  raw"
                                                            >
                                                                {x.options.loading && (
                                                                    <div className="absolute z-10 text-center w-full h-full bg-gray-200 cursor-pointer  bg-opacity-25 ">
                                                                        <LoaderSpinner
                                                                            className="mt-3"
                                                                            variant="button"
                                                                        ></LoaderSpinner>
                                                                    </div>
                                                                )}
                                                                {!x.options.trackLoaded && (
                                                                    <div
                                                                        className="absolute z-10 w-full h-full bg-gray-200 cursor-pointer opacity-25 "
                                                                        onClick={() => {
                                                                            if (!x.options.trackLoaded) {
                                                                                this.trackDisplay(x.uuid, true);
                                                                            }
                                                                        }}
                                                                    ></div>
                                                                )}
                                                                <div
                                                                    onClick={async () => {
                                                                        if (!x.options.trackLoaded) {
                                                                            await this.trackDisplay(x.uuid, true);
                                                                        }
                                                                        this.goToVesel(x.uuid);
                                                                    }}
                                                                    className={twclsx(
                                                                        'cursor-pointer',
                                                                        'pt-1',

                                                                        !x.options.display && 'opacity-25'
                                                                    )}
                                                                >
                                                                    <div>
                                                                        <Image
                                                                            alt={getFlagName(x?.mmsi) || ''}
                                                                            className="float-left mt-1 mr-1"
                                                                            src={getFlagImage(x.mmsi) || ''}
                                                                        />
                                                                        <span
                                                                            className={twclsx(
                                                                                (!hasPermission(
                                                                                    this.props.user,
                                                                                    this.props.port.id,
                                                                                    ['identifyTransits']
                                                                                ) ||
                                                                                    !this.state.switchs.identifyTransits
                                                                                        .value) &&
                                                                                    'blurText'
                                                                            )}
                                                                        >
                                                                            {x.vessel.name}
                                                                        </span>
                                                                    </div>
                                                                    <div className="capitalize text-sm leading-3">
                                                                        <span
                                                                            className={twclsx(
                                                                                (!hasPermission(
                                                                                    this.props.user,
                                                                                    this.props.port.id,
                                                                                    ['identifyTransits']
                                                                                ) ||
                                                                                    !this.state.switchs.identifyTransits
                                                                                        .value) &&
                                                                                    'blurText'
                                                                            )}
                                                                        >
                                                                            {x.mmsi}
                                                                        </span>{' '}
                                                                        {translate(x.vessel.vessel_category.label)}{' '}
                                                                    </div>
                                                                </div>
                                                                {
                                                                    // TODO //make the two icons at the center
                                                                    // TODO //add tooltip for this action
                                                                    // TODO // add backslash to the icon and keep make it black
                                                                }
                                                                <div className=" rawActionsStaticRight  no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                    <div>
                                                                        {x.options.display ? (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer text-gray-600 ">
                                                                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer text-gray-400 ">
                                                                                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        {x.options.drawTrack && x.options.display ? (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer  text-gray-600">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer  text-gray-400">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className=" rawActionsRight no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                    <div>
                                                                        {!x.options.display ? (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDisplay(x.uuid, true);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    html={translate('show vessel')}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDisplay(x.uuid, false);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    html={translate('hide vessel')}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        {x.options.drawTrack && x.options.display ? (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDrawTrack(x.uuid, false);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    html={translate(
                                                                                        'hide vessel track'
                                                                                    )}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDrawTrack(x.uuid, true);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    html={translate(
                                                                                        'show vessel track'
                                                                                    )}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })}
                                        </div>
                                        <div>
                                            {this.state.tracks
                                                .filter((x) => x.options.group === group.name)
                                                .sort((a, b) => {
                                                    if (a.vessel.name < b.vessel.name) return -1;
                                                    if (a.vessel.name > b.vessel.name) return 1;
                                                    return 0;
                                                })

                                                .filter((x) => x.options.moving === false)
                                                .map((x, i) => {
                                                    return (
                                                        <>
                                                            {i === 0 && (
                                                                <div className="bg-gray-100 border-b raw relative  border-gray-300 py-1 flex justify-between  ">
                                                                    <div className=" flex items-center gap-1">
                                                                        <div>
                                                                            <Icon>
                                                                                <path d="M2 20a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1"></path>
                                                                                <path d="M4 18l-1 -5h18l-2 4"></path>
                                                                                <path d="M5 13v-6h8l4 6"></path>
                                                                                <path d="M7 7v-4h-1"></path>
                                                                            </Icon>
                                                                        </div>
                                                                        <div className="font-bold ">
                                                                            {translate('Berthed/Anchored vessels')}
                                                                        </div>
                                                                    </div>

                                                                    <div className="rawActionsStaticRight hidden right-0 h-full flex justify-items-center gap-1 pt-2 pr-1 ">
                                                                        <div>
                                                                            {!group.displayNotMoving ? (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {!group.drawTrackNotMoving ? (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            ) : (
                                                                                <div>
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-black">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <div className="rawActionsRight right-0 h-full flex justify-items-center gap-1 pt-2 pr-1 pl-10 ">
                                                                        <div>
                                                                            {!group.displayNotMoving ? (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDisplayGroup(
                                                                                            group.name,
                                                                                            false,
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`displayGroupBerthedVessels`}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                        <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        id="displayGroupBerthedVessels"
                                                                                        html={`${translate(
                                                                                            'Show all vessels'
                                                                                        )}`}
                                                                                        className="z-20"
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDisplayGroup(
                                                                                            group.name,
                                                                                            false,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`displayGroupBerthedVessels`}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                        <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        id="displayGroupBerthedVessels"
                                                                                        html={`${translate(
                                                                                            'Hide all vessels'
                                                                                        )}`}
                                                                                        className="z-20"
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            {!group.drawTrackNotMoving ? (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrackGroup(
                                                                                            group.name,
                                                                                            false,
                                                                                            true
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`trackGroupBerthedVessels`}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        id="trackGroupBerthedVessels"
                                                                                        html={`${translate(
                                                                                            'Show all vessels tracks'
                                                                                        )}`}
                                                                                        className="z-20"
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => {
                                                                                        this.trackDrawTrackGroup(
                                                                                            group.name,
                                                                                            false,
                                                                                            false
                                                                                        );
                                                                                    }}
                                                                                    data-tooltip-id={`trackGroupBerthedVessels`}
                                                                                >
                                                                                    <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                        <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                        <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                        <path d="M3 3l18 18"></path>
                                                                                    </Icon>
                                                                                    <Tooltip
                                                                                        id="trackGroupBerthedVessels"
                                                                                        html={`${translate(
                                                                                            'Hide all vessels tracks'
                                                                                        )}`}
                                                                                        className="z-20"
                                                                                        place={'bottom-end'}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div
                                                                key={x.uuid}
                                                                className="flex justify-between border-b border-gray-300 relative pb-1 raw"
                                                            >
                                                                {x.options.loading && (
                                                                    <div className="absolute z-10 text-center w-full h-full bg-gray-200 cursor-pointer  bg-opacity-25 ">
                                                                        <LoaderSpinner
                                                                            className="mt-3"
                                                                            variant="button"
                                                                        ></LoaderSpinner>
                                                                    </div>
                                                                )}
                                                                {!x.options.trackLoaded && (
                                                                    <div
                                                                        className="absolute z-10 w-full h-full bg-gray-200 cursor-pointer opacity-25 "
                                                                        onClick={() => {
                                                                            if (!x.options.trackLoaded) {
                                                                                this.trackDisplay(x.uuid, true);
                                                                            }
                                                                        }}
                                                                    ></div>
                                                                )}
                                                                <div
                                                                    onClick={async () => {
                                                                        if (!x.options.trackLoaded) {
                                                                            await this.trackDisplay(x.uuid, true);
                                                                        }
                                                                        this.goToVesel(x.uuid);
                                                                    }}
                                                                    className={twclsx(
                                                                        'cursor-pointer',
                                                                        'pt-1',

                                                                        !x.options.display && 'opacity-25'
                                                                    )}
                                                                >
                                                                    <div>
                                                                        <Image
                                                                            alt={getFlagName(x?.mmsi) || ''}
                                                                            className="float-left mt-1 mr-1"
                                                                            src={getFlagImage(x.mmsi) || ''}
                                                                        />
                                                                        <span
                                                                            className={twclsx(
                                                                                (!hasPermission(
                                                                                    this.props.user,
                                                                                    this.props.port.id,
                                                                                    ['identifyTransits']
                                                                                ) ||
                                                                                    !this.state.switchs.identifyTransits
                                                                                        .value) &&
                                                                                    'blurText'
                                                                            )}
                                                                        >
                                                                            {x.vessel.name}
                                                                        </span>
                                                                    </div>
                                                                    <div className="capitalize text-sm leading-3">
                                                                        <span
                                                                            className={twclsx(
                                                                                (!hasPermission(
                                                                                    this.props.user,
                                                                                    this.props.port.id,
                                                                                    ['identifyTransits']
                                                                                ) ||
                                                                                    !this.state.switchs.identifyTransits
                                                                                        .value) &&
                                                                                    'blurText'
                                                                            )}
                                                                        >
                                                                            {x.mmsi}
                                                                        </span>{' '}
                                                                        {translate(x.vessel.vessel_category.label)}{' '}
                                                                    </div>
                                                                </div>
                                                                {
                                                                    // TODO //make the two icons at the center
                                                                    // TODO //add tooltip for this action
                                                                    // TODO // add backslash to the icon and keep make it black
                                                                }
                                                                <div className=" rawActionsStaticRight  no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                    <div>
                                                                        {x.options.display ? (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer text-gray-600 ">
                                                                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer text-gray-400 ">
                                                                                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        {x.options.drawTrack && x.options.display ? (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer  text-gray-600">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                                                <Icon className="w-4 h-4 cursor-pointer  text-gray-400">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className=" rawActionsRight no-select flex whitespace-no-wrap text-right h-full right-0 pr-1 pl-10 leading-5 font-medium items-center">
                                                                    <div>
                                                                        {!x.options.display ? (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDisplay(x.uuid, true);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                                                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    html={translate('show vessel')}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDisplay(x.uuid, false);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path>
                                                                                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-desplay-tooltip`}
                                                                                    html={translate('hide vessel')}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div>
                                                                        {x.options.drawTrack && x.options.display ? (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDrawTrack(x.uuid, false);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M3 3l18 18"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    html={translate(
                                                                                        'hide vessel track'
                                                                                    )}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                onClick={() => {
                                                                                    this.trackDrawTrack(x.uuid, true);
                                                                                }}
                                                                                data-tooltip-id={`${x.uuid}-vessel-track-tooltip`}
                                                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                                            >
                                                                                <Icon className="w-4 h-4 cursor-pointer text-white">
                                                                                    <path d="M4 16l6 -7l5 5l5 -6"></path>
                                                                                    <path d="M15 14m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M10 9m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M4 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                    <path d="M20 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                                                </Icon>
                                                                                <Tooltip
                                                                                    className="z-40"
                                                                                    id={`${x.uuid}-vessel-track-tooltip`}
                                                                                    html={translate(
                                                                                        'show vessel track'
                                                                                    )}
                                                                                    place={'bottom-end'}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })}
                                        </div>
                                    </MenuGroup>
                                );
                            })}
                    </>
                )}
            </div>
        );
    }
}

const timeVesselsTabStateToProps = (state: RootState) => ({
    dateTimeFormat: state.port.dateTimeFormat,
    layout: state.layout.layout,
    system: state.port.system,
    port: state.port.port,
    mainControls: state.layout.mainControls,
    openModelFunction: state.layout.openModelFunction,
    user: state.user.user,
    switchs: state.layout.switchs,
    mapControls: state.map.mapControls,
});

const timeVesselsTabDispatchToProps = (dispatch: Dispatch) => ({
    setSystemLoadingAction: bindActionCreators(setSystemLoadingAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setMenuControlsAction: bindActionCreators(setMenuControlsAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(timeVesselsTabStateToProps, timeVesselsTabDispatchToProps)(TimeVesselsTab);
